// src/index.js
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import ReactGA from 'react-ga4';
import { BrowserRouter, useLocation } from 'react-router-dom';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './authConfig';

import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { brand, ink, motion, radius, surface, type as typeScale } from './designTokens';
// ─── Read your GA4 ID from env ────────────────────────────────────────────────
const GA4_ID = process.env.REACT_APP_GA4_PROPERTY_ID || process.env.GA4_PROPERTY_ID;
if (GA4_ID) {
  ReactGA.initialize(GA4_ID);
}

// ─── Corporate MUI theme ───────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: ink[900], contrastText: surface.card },
    secondary: { main: brand.gold, contrastText: surface.card },
    background: { default: surface.page, paper: surface.card },
    text: { primary: ink[900], secondary: ink[500] },
    divider: surface.line,
  },
  shape: { borderRadius: radius.md },
  typography: {
    fontFamily: '"Oswald","Helvetica","Arial",sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 300,
    fontWeightMedium: 500,
    h4: { fontWeight: 400, fontSize: typeScale.display, letterSpacing: '-0.01em' },
    h5: { fontWeight: 400, fontSize: typeScale.h1 },
    h6: { fontWeight: 500, fontSize: typeScale.h2 },
    body1: { fontSize: typeScale.body },
    body2: { fontSize: typeScale.small },
    caption: { fontSize: typeScale.micro },
    button: { textTransform: 'none', fontWeight: 400 },
  },
  components: {
    MuiAppBar: {
      styleOverrides: { root: { backgroundColor: 'transparent', boxShadow: 'none' } },
    },
    MuiPaper: {
      styleOverrides: { root: { borderRadius: radius.md, backgroundImage: 'none' } },
    },
    // MUI's default icon blue was the single loudest off-brand element in the
    // console; icons inherit ink unless a component opts into a status color.
    MuiSvgIcon: { styleOverrides: { root: { color: ink[700] } } },
    MuiIconButton: { styleOverrides: { root: { color: ink[700] } } },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: surface.line,
          fontSize: typeScale.body,
          // Figures line up column-to-column instead of jittering.
          fontVariantNumeric: 'tabular-nums',
        },
        head: {
          backgroundColor: surface.sunken,
          color: ink[500],
          fontSize: typeScale.micro,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fontWeight: 500,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: surface.sunken } },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radius.sm, fontSize: typeScale.micro, fontWeight: 500, height: 22 },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: radius.sm, transition: `background-color ${motion.fast}, border-color ${motion.fast}` },
        containedPrimary: { '&:hover': { backgroundColor: ink[700] } },
      },
    },
    // Focus must stay visible — this is an operations tool used by keyboard.
    MuiCssBaseline: {
      styleOverrides: {
        ':focus-visible': { outline: `2px solid ${brand.gold}`, outlineOffset: '2px' },
        '@media (prefers-reduced-motion: reduce)': {
          '*': { animationDuration: '0.001ms !important', transitionDuration: '0.001ms !important' },
        },
      },
    },
  },
});

// ─── Wraps App so we can listen to route changes for GA ───────────────────────
function GAListener({ children }) {
  const location = useLocation();
useEffect(() => {
   if (GA4_ID) {
     ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
   }
   // no cleanup needed:
   return undefined;
 }, [location]);
  return children;
}

const msalInstance = new PublicClientApplication(msalConfig);

const root = ReactDOM.createRoot(document.getElementById('root'));

// msal-browser v3+ requires initialize() to resolve before the instance is
// used anywhere (MsalProvider included) — skipping this leaves MSAL's
// internal interaction status stuck at "Startup" forever, which is why the
// Microsoft sign-in screen was spinning on "Loading…" with no console error.
msalInstance.initialize().then(() => {
  root.render(
    <MsalProvider instance={msalInstance}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <GAListener>
            <App />
          </GAListener>
        </BrowserRouter>
      </ThemeProvider>
    </MsalProvider>
  );

  reportWebVitals();
});
